import React, { useEffect, useMemo } from 'react';
import { Form, Formik } from 'formik';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import BaseForm from '../../../../../../../../components/shared/form-controls/base-form';
import FormAutocomplete from '../../../../../../../../components/shared/form-controls/form-autocomplete';
import { IdNameModel, SIdNameModel, ZohoConfiguration } from '../../../../../../../../features/integrations/zoho/types';
import { useAppDispatch, useAppSelector } from '../../../../../../../../hooks/hooks';
import { configure, get as getZohoConfiguration } from '../../../../../../../../features/integrations/zoho';
import { NAVIGATION_ROUTES } from '../../../../../../../../routes/NavigationRoute';
import { getIntegrations } from '../../../../../../../../features/settings/integrations';
import { Integration } from '../../../../../../../../features/settings/integrations/types';
import { Lx } from '../../../../../../../../i18n/consts';
import { DropdownField } from '../../../../../../../../components/shared/nextgen/dropdown-field';
import { canManageIntegrations } from '../../../../../../../../features/profile/entityPermissions';

interface EditForm {
    employeesForNotification: Array<IdNameModel>;
    employeesForSynchronization: Array<IdNameModel>;
    projectsForSynchronization: Array<IdNameModel>;
    organizationId: SIdNameModel;
}

interface LocationState {
    integration?: Integration;
}

const ZohoIntegrationConfigurationPage = () => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const profile = useAppSelector(store => store.profile);
    const integrations = useAppSelector(store => store.integrations.items);
    const zohoIntegration = useAppSelector(store => store.integrationZoho);
    const state = (location.state as LocationState) ?? {};
    const integration = state.integration;
    const isConfigurationLoaded = zohoIntegration.availableOrganizations.length > 0;
    const canEditIntegrations = canManageIntegrations(profile);

    useEffect(() => {
        if (!canEditIntegrations) {
            navigate(NAVIGATION_ROUTES.SETTINGS_INTEGRATIONS, { replace: true });
            return;
        }

        if (!integration && !integrations.length) {
            dispatch(getIntegrations());
        }
    }, [canEditIntegrations, dispatch, integration, integrations.length, navigate]);

    useEffect(() => {
        if (!canEditIntegrations || (!integration && integrations.length)) {
            navigate(NAVIGATION_ROUTES.SETTINGS_INTEGRATIONS, { replace: true });
        }
    }, [canEditIntegrations, integration, integrations.length, navigate]);

    useEffect(() => {
        if (integration && !isConfigurationLoaded) {
            dispatch(getZohoConfiguration({ endpoint: integration.endpoints.get }));
        }
    }, [integration, isConfigurationLoaded, dispatch]);

    const initialValues: EditForm = useMemo(() => {
        const configuration = zohoIntegration.configuration;
        const organization = zohoIntegration.availableOrganizations.find(x => x.id === configuration.organizationId) ?? zohoIntegration.availableOrganizations[0];

        return {
            employeesForNotification: zohoIntegration.availableEmployeesForNotifications.filter(x => configuration.employeesForNotification.includes(x.id)),
            employeesForSynchronization: zohoIntegration.availableEmployeesForSynchronization.filter(x => configuration.employeesForSynchronization.includes(x.id)),
            projectsForSynchronization: zohoIntegration.availableProjects.filter(x => configuration.projectsForSynchronization.includes(x.id)),
            organizationId: organization
        };
    }, [integration, isConfigurationLoaded, zohoIntegration]);

    const handleClose = () => {
        navigate(NAVIGATION_ROUTES.SETTINGS_INTEGRATIONS);
    };

    const save = async (values: EditForm) => {
        const organizationId = values.organizationId?.id ?? zohoIntegration.availableOrganizations[0]?.id ?? '';
        const configuration: ZohoConfiguration = {
            organizationId,
            synchronizationType: 0,
            employeesForSynchronization: values.employeesForSynchronization.map(x => x.id),
            employeesForNotification: values.employeesForNotification.map(x => x.id),
            projectsForSynchronization: values.projectsForSynchronization.map(x => x.id)
        };

        await dispatch(configure({ endpoint: integration?.endpoints.configure ?? '', configuration })).unwrap();
        await dispatch(getIntegrations());
    };

    const savingTitle = intl.formatMessage({ id: Lx.Integration.TITLE_FORMAT }, { s: 'Zoho' });

    if (!canEditIntegrations) {
        return null;
    }

    return (
        <Formik initialValues={initialValues} enableReinitialize onSubmit={save}>
            <Form>
                <BaseForm
                    contentGap={24}
                    formType='edit'
                    formHeader={Lx.Integration.CONFIGURE}
                    elementName='Zoho'
                    savingTitle={savingTitle}
                    onClose={handleClose}
                    onAfterSave={handleClose}>
                    <div style={{ width: '100%', maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        <DropdownField
                            required
                            label={intl.formatMessage({ id: Lx.Integration.Zoho.ORGANIZATIONS })}
                            name='organizationId'
                            items={zohoIntegration.availableOrganizations}
                        />
                        <FormAutocomplete
                            required
                            placeHolder={intl.formatMessage({ id: Lx.Integration.SELECT_EMPLOYEES })}
                            label={intl.formatMessage({ id: Lx.Integration.Zoho.EMPLOYEE_NOTIFY })}
                            name='employeesForNotification'
                            items={zohoIntegration.availableEmployeesForNotifications}
                            selectedItems={zohoIntegration.configuration.employeesForNotification.map(x => ({ id: x, name: '', isDefault: false }))}
                        />
                        <FormAutocomplete
                            required={false}
                            placeHolder={intl.formatMessage({ id: Lx.Integration.SELECT_PROJECTS })}
                            label={intl.formatMessage({ id: 'projects' })}
                            name='projectsForSynchronization'
                            items={zohoIntegration.availableProjects}
                            selectedItems={zohoIntegration.configuration.projectsForSynchronization.map(x => ({ id: x, name: '', isDefault: false }))}
                        />
                        <FormAutocomplete
                            required={false}
                            placeHolder={intl.formatMessage({ id: Lx.Integration.SELECT_EMPLOYEES })}
                            label={intl.formatMessage({ id: 'employees' })}
                            name='employeesForSynchronization'
                            items={zohoIntegration.availableEmployeesForSynchronization}
                            selectedItems={zohoIntegration.configuration.employeesForSynchronization.map(x => ({ id: x, name: '', isDefault: false }))}
                        />
                    </div>
                </BaseForm>
            </Form>
        </Formik>
    );
}

export default ZohoIntegrationConfigurationPage;
